package com.heelion.farmafreelance.ui

import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.*
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.lerp
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.ui.platform.LocalUriHandler
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import kotlinx.coroutines.launch
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toLocalDateTime
import com.heelion.farmafreelance.agent.Province
import com.heelion.farmafreelance.models.*
import org.jetbrains.compose.resources.stringResource
import farmafreelance.composeapp.generated.resources.*
import com.heelion.farmafreelance.utils.TimeUtils
import com.heelion.farmafreelance.voice.VoiceIntent
import kotlinx.datetime.Instant
import androidx.compose.foundation.background
import androidx.compose.ui.draw.blur

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun FarmacistaScreen(
    viewModel: AuthViewModel,
    pendingIntent: VoiceIntent? = null,
    onPendingIntentConsumed: () -> Unit = {},
    onVoiceIntentRouted: (VoiceIntent) -> Unit = {}
) {
    val subStatus = viewModel.subscriptionStatus
    var searchQuery by remember { mutableStateOf("") }
    var searchRegione by remember { mutableStateOf("") }
    var searchProvincia by remember { mutableStateOf("") }
    var searchComune by remember { mutableStateOf("") }
    var searchCap by remember { mutableStateOf("") }
    
    var startDate by remember { mutableStateOf<Long?>(null) }
    var endDate by remember { mutableStateOf<Long?>(null) }
    
    var showRegioneMenu by remember { mutableStateOf(false) }
    var showProvinciaMenu by remember { mutableStateOf(false) }
    var showDatePicker by remember { mutableStateOf(false) }
    
    // Chi cerca la farmacia: farmacisti o infermieri (tabelle e competenze diverse), dal selettore in alto.
    val professioneCercata = viewModel.professioneVisibile
    var searchCompetenza by remember { mutableStateOf("") }
    var showCompetenzaMenu by remember { mutableStateOf(false) }
    val competenzePredefinite = if (professioneCercata == Professione.INFERMIERE) competenzeDi(UserRole.INFERMIERE)
        else listOf("Elettrocardiogramma", "Tamponi", "Galenica", "Deblistering", "Autoanalisi", "Omeopatia", "Veterinaria", "Noleggi Sanitari", "E-Commerce", "Social Media")

    val regioni = viewModel.listaRegioni
    val province = viewModel.listaProvince

    var searchResults by remember { mutableStateOf(listOf<Professionista>()) }
    val scope = rememberCoroutineScope()

    fun performSearch() {
        scope.launch {
            // Letta qui e non dal valore della composizione: un comando a voce può averla appena cambiata.
            searchResults = if (viewModel.professioneVisibile == Professione.INFERMIERE) {
                viewModel.searchInfermieri(
                    query = searchQuery,
                    comune = searchComune,
                    startDate = startDate,
                    endDate = endDate,
                    competenza = searchCompetenza,
                    provincia = searchProvincia,
                    regione = searchRegione,
                    cap = searchCap
                )
            } else {
                viewModel.searchFarmacisti(
                    query = searchQuery,
                    comune = searchComune,
                    cap = searchCap,
                    startDate = startDate,
                    endDate = endDate,
                    competenza = searchCompetenza,
                    provincia = searchProvincia,
                    regione = searchRegione
                )
            }
        }
    }

    // Corpo del comando vocale/testuale estratto in una funzione a sé: così sia il pulsante
    // microfono di questa schermata sia un intento già riconosciuto altrove (es. dalla Home,
    // instradato qui tramite [pendingIntent]) eseguono esattamente la stessa logica.
    fun gestisciIntentVocale(text: String, intent: VoiceIntent): String {
        return when (intent) {
            is VoiceIntent.CercaFarmacisti -> {
                // Ogni ricerca a voce riparte da zero, senza sommarsi a un comune/nome
                // impostato da una ricerca precedente.
                searchComune = ""
                searchQuery = ""
                // "cerca infermieri a Monza": si passa agli infermieri (le competenze cambiano),
                // se la farmacia lavora con loro.
                if (intent.professione == Professione.INFERMIERE && !viewModel.infermieriAttivi) {
                    return "Per cercare infermieri attiva prima \"Servizi infermieristici\" nel tuo Profilo."
                }
                if (viewModel.professioneVisibile != intent.professione) {
                    viewModel.scegliProfessione(intent.professione)
                    searchCompetenza = ""
                }
                // Come Farmy: nel database le province sono sigle ("in provincia di Roma" -> "RM") e un
                // capoluogo ("a Roma") cerca in tutta la sua provincia; la regione va nel suo filtro.
                searchRegione = intent.regione.orEmpty()
                if (searchRegione.isNotEmpty()) viewModel.updateProvincePerRegione(searchRegione)
                searchProvincia = intent.provincia?.let { Province.sigla(it) ?: it }
                    ?: intent.comune?.let { Province.sigla(it) }.orEmpty()
                intent.comune?.takeIf { Province.sigla(it) == null }?.let { searchComune = it }
                searchCap = ""
                intent.query?.let { searchQuery = it }
                performSearch()
                "Ho sentito: \"$text\". Ho cercato in base a quello che ho capito."
            }
            is VoiceIntent.Unrecognized -> "Ho sentito: \"$text\". Non l'ho capito, riprova o usa i filtri qui sotto."
            // Non di competenza di questa schermata: lo rimbalzo al router centrale.
            else -> {
                onVoiceIntentRouted(intent)
                "Ho sentito: \"$text\". Ti porto alla sezione giusta..."
            }
        }
    }

    // Cambiando farmacisti/infermieri nel selettore si rifà la ricerca con gli stessi filtri
    // (le competenze cambiano da una professione all'altra). Non al primo ingresso nella pagina.
    var professioneMostrata by remember { mutableStateOf(professioneCercata) }
    LaunchedEffect(professioneCercata) {
        if (professioneMostrata != professioneCercata) {
            professioneMostrata = professioneCercata
            searchCompetenza = ""
            performSearch()
        }
    }

    LaunchedEffect(pendingIntent) {
        val i = pendingIntent
        if (i != null) {
            gestisciIntentVocale("(dalla Home)", i)
            onPendingIntentConsumed()
        }
    }

    val themeColorHex = viewModel.selectedThemeColor.hex.removePrefix("0x").toLong(16)
    val themeColor = Color(themeColorHex)
    val allLabel = stringResource(Res.string.all_filter)

    Column(modifier = Modifier.fillMaxSize()) {
        val days = subStatus.daysRemaining
        if (subStatus.isTrial && days != null) {
            TrialBanner(days)
        }
        
        Column(modifier = Modifier.fillMaxSize().padding(16.dp)) {
            // --- SEZIONE CRUSCOTTO (UNIFORME) ---
            Card(
                modifier = Modifier.fillMaxWidth(),
                colors = CardDefaults.cardColors(
                    containerColor = lerp(Color.White, themeColor, 0.15f)
                ),
                shape = RoundedCornerShape(16.dp),
                border = androidx.compose.foundation.BorderStroke(
                    width = 0.5.dp,
                    color = if (themeColor == Color.White) Color.LightGray else themeColor.copy(alpha = 0.2f)
                ),
                elevation = CardDefaults.cardElevation(defaultElevation = 2.dp)
            ) {
                Row(modifier = Modifier.fillMaxWidth().height(IntrinsicSize.Min)) {
                    // BORDO LATERALE A SINISTRA
                    Box(
                        modifier = Modifier
                            .width(6.dp)
                            .fillMaxHeight()
                            .background(if (themeColor == Color.White) Color.Gray else themeColor)
                    )
                    Column(modifier = Modifier.padding(16.dp)) {
                        Text(
                            text = stringResource(Res.string.tab_search),
                            style = MaterialTheme.typography.headlineMedium,
                            fontWeight = FontWeight.Bold,
                            color = if (themeColor == Color.White) Color.Black else themeColor
                        )
                        Text(
                            text = if (professioneCercata == Professione.INFERMIERE) "Cerca Infermiere" else stringResource(Res.string.search_pharmacists_title),
                            style = MaterialTheme.typography.bodySmall,
                            color = Color.Gray
                        )
                    }
                }
            }

            Spacer(modifier = Modifier.height(16.dp))

            // Farmacisti o infermieri li sceglie il selettore in alto: qui si ricorda solo chi si sta cercando.
            if (viewModel.infermieriAttivi) {
                Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                    Text("Stai cercando:", style = MaterialTheme.typography.bodyMedium, fontWeight = FontWeight.Bold)
                    EtichettaProfessione(professioneCercata)
                }
                Spacer(modifier = Modifier.height(8.dp))
            }

            VoiceSearchButton(
                hint = if (professioneCercata == Professione.INFERMIERE) "Cerca a voce (es. \"cercami un infermiere per i prelievi a Monza\")"
                    else "Cerca a voce (es. \"cerca farmacisti a Monza\")",
                onIntent = { text, intent -> gestisciIntentVocale(text, intent) },
                onError = {}
            )

            OutlinedTextField(
                value = searchQuery,
                onValueChange = {
                    searchQuery = it
                    performSearch()
                },
                label = { Text(if (professioneCercata == Professione.INFERMIERE) "Nome dell'infermiere" else stringResource(Res.string.pharmacist_name_label)) },
                modifier = Modifier.fillMaxWidth(),
                leadingIcon = { Icon(Icons.Default.Search, contentDescription = null) },
                shape = RoundedCornerShape(12.dp),
                singleLine = true
            )

            // FILTRI GEOGRAFICI A CASCATA ALLINEATI
            Row(modifier = Modifier.fillMaxWidth().padding(top = 8.dp), horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                // REGIONE
                ExposedDropdownMenuBox(
                    expanded = showRegioneMenu,
                    onExpandedChange = {
                        showRegioneMenu = !showRegioneMenu
                        if (showRegioneMenu && regioni.isEmpty()) viewModel.loadRegioni()
                    },
                    modifier = Modifier.weight(1f)
                ) {
                    OutlinedTextField(
                        value = searchRegione.ifEmpty { allLabel },
                        onValueChange = {},
                        readOnly = true,
                        label = { Text(stringResource(Res.string.region_label_short)) },
                        trailingIcon = { ExposedDropdownMenuDefaults.TrailingIcon(expanded = showRegioneMenu) },
                        modifier = Modifier.menuAnchor(MenuAnchorType.PrimaryNotEditable, true).fillMaxWidth(),
                        shape = RoundedCornerShape(12.dp)
                    )
                    ExposedDropdownMenu(expanded = showRegioneMenu, onDismissRequest = { showRegioneMenu = false }) {
                        DropdownMenuItem(text = { Text(allLabel) }, onClick = { 
                            searchRegione = ""
                            searchProvincia = ""
                            showRegioneMenu = false
                            performSearch()
                        })
                        regioni.forEach { r ->
                            DropdownMenuItem(text = { Text(r) }, onClick = { 
                                searchRegione = r
                                searchProvincia = ""
                                viewModel.updateProvincePerRegione(r)
                                showRegioneMenu = false
                                performSearch()
                            })
                        }
                    }
                }

                // PROVINCIA
                ExposedDropdownMenuBox(
                    expanded = showProvinciaMenu,
                    onExpandedChange = { if(searchRegione.isNotEmpty()) showProvinciaMenu = !showProvinciaMenu },
                    modifier = Modifier.weight(1f)
                ) {
                    OutlinedTextField(
                        value = searchProvincia.ifEmpty { allLabel },
                        onValueChange = {},
                        readOnly = true,
                        enabled = searchRegione.isNotEmpty(),
                        label = { Text(stringResource(Res.string.province_label_short)) },
                        trailingIcon = { ExposedDropdownMenuDefaults.TrailingIcon(expanded = showProvinciaMenu) },
                        modifier = Modifier.menuAnchor(MenuAnchorType.PrimaryNotEditable, true).fillMaxWidth(),
                        shape = RoundedCornerShape(12.dp)
                    )
                    if (province.isNotEmpty()) {
                        ExposedDropdownMenu(expanded = showProvinciaMenu, onDismissRequest = { showProvinciaMenu = false }) {
                            DropdownMenuItem(text = { Text(allLabel) }, onClick = {
                                searchProvincia = ""
                                showProvinciaMenu = false
                                performSearch()
                            })
                            province.forEach { p ->
                                DropdownMenuItem(text = { Text(p) }, onClick = { 
                                    searchProvincia = p
                                    showProvinciaMenu = false
                                    performSearch()
                                })
                            }
                        }
                    }
                }
            }

            Row(modifier = Modifier.fillMaxWidth().padding(top = 8.dp), horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                OutlinedTextField(
                    value = searchComune,
                    onValueChange = { 
                        searchComune = it
                        performSearch()
                    },
                    label = { Text(stringResource(Res.string.city_label_short), fontSize = 11.sp) },
                    modifier = Modifier.weight(1f),
                    shape = RoundedCornerShape(12.dp),
                    singleLine = true
                )
                OutlinedTextField(
                    value = searchCap,
                    onValueChange = { 
                        searchCap = it
                        performSearch()
                    },
                    label = { Text(stringResource(Res.string.cap_label_short), fontSize = 11.sp) },
                    modifier = Modifier.width(90.dp),
                    shape = RoundedCornerShape(12.dp),
                    singleLine = true
                )
            }

            // FILTRO COMPETENZE
            PremiumGuard(viewModel) {
                ExposedDropdownMenuBox(
                    expanded = showCompetenzaMenu,
                    onExpandedChange = { showCompetenzaMenu = !showCompetenzaMenu },
                    modifier = Modifier.fillMaxWidth().padding(top = 8.dp)
                ) {
                    OutlinedTextField(
                        value = searchCompetenza.ifEmpty { allLabel },
                        onValueChange = {},
                        readOnly = true,
                        label = { Text(stringResource(Res.string.skills_filter_label), fontSize = 11.sp) },
                        trailingIcon = { ExposedDropdownMenuDefaults.TrailingIcon(expanded = showCompetenzaMenu) },
                        modifier = Modifier.menuAnchor(MenuAnchorType.PrimaryNotEditable, true).fillMaxWidth(),
                        shape = RoundedCornerShape(12.dp)
                    )
                    ExposedDropdownMenu(expanded = showCompetenzaMenu, onDismissRequest = { showCompetenzaMenu = false }) {
                        DropdownMenuItem(text = { Text(allLabel) }, onClick = { 
                            searchCompetenza = ""
                            showCompetenzaMenu = false
                            performSearch()
                        })
                        competenzePredefinite.forEach { skill ->
                            DropdownMenuItem(text = { Text(skill) }, onClick = { 
                                searchCompetenza = skill
                                showCompetenzaMenu = false
                                performSearch()
                            })
                        }
                    }
                }
            }

            // PERIODO TEMPORALE
            val dateText = if (startDate != null && endDate != null) {
                val s = Instant.fromEpochMilliseconds(startDate!!).toLocalDateTime(TimeZone.UTC).date
                val e = Instant.fromEpochMilliseconds(endDate!!).toLocalDateTime(TimeZone.UTC).date
                "${s.dayOfMonth}/${s.monthNumber} - ${e.dayOfMonth}/${e.monthNumber}"
            } else stringResource(Res.string.any_period)

            Button(
                onClick = { showDatePicker = true },
                modifier = Modifier.fillMaxWidth().padding(top = 8.dp),
                shape = RoundedCornerShape(12.dp),
                colors = ButtonDefaults.buttonColors(
                    containerColor = if (startDate != null) themeColor else Color.Gray,
                    contentColor = Color.White
                )
            ) {
                Icon(Icons.Default.DateRange, contentDescription = null, modifier = Modifier.size(18.dp))
                Spacer(Modifier.width(8.dp))
                Text(dateText.uppercase(), fontWeight = FontWeight.Bold)
            }

            if (startDate != null) {
                TextButton(
                    onClick = {
                        startDate = null
                        endDate = null
                        performSearch()
                    },
                    modifier = Modifier.align(Alignment.CenterHorizontally)
                ) {
                    Icon(Icons.Default.Clear, contentDescription = "Pulisci date", modifier = Modifier.size(16.dp))
                    Spacer(Modifier.width(4.dp))
                    Text("PULISCI DATE", fontSize = 10.sp)
                }
            }

            if (showDatePicker) {
                val dateRangePickerState = rememberDateRangePickerState()
                DatePickerDialog(
                    onDismissRequest = { showDatePicker = false },
                    confirmButton = {
                        TextButton(
                            onClick = {
                                startDate = dateRangePickerState.selectedStartDateMillis
                                endDate = dateRangePickerState.selectedEndDateMillis
                                showDatePicker = false
                                performSearch()
                            },
                            enabled = dateRangePickerState.selectedStartDateMillis != null
                        ) { Text(stringResource(Res.string.generate_shifts_caps)) }
                    },
                    dismissButton = {
                        TextButton(onClick = { showDatePicker = false }) { Text(stringResource(Res.string.cancel_button)) }
                    }
                ) {
                    val hasFestive = remember(dateRangePickerState.selectedStartDateMillis, dateRangePickerState.selectedEndDateMillis) {
                        val s = dateRangePickerState.selectedStartDateMillis
                        val e = dateRangePickerState.selectedEndDateMillis
                        if (s == null) false
                        else {
                            val start = Instant.fromEpochMilliseconds(s).toLocalDateTime(TimeZone.UTC).date
                            val end = if (e != null) Instant.fromEpochMilliseconds(e).toLocalDateTime(TimeZone.UTC).date else start
                            TimeUtils.isFestivo(start) || TimeUtils.isFestivo(end)
                        }
                    }

                    Column(
                        modifier = Modifier.fillMaxWidth(),
                        horizontalAlignment = Alignment.CenterHorizontally
                    ) {
                        DateRangePicker(
                            state = dateRangePickerState,
                            modifier = Modifier.weight(1f).padding(top = 8.dp),
                            title = { Text(stringResource(Res.string.select_period_title), modifier = Modifier.padding(start = 16.dp, top = 16.dp, bottom = 8.dp), style = MaterialTheme.typography.labelLarge) },
                            headline = null
                        )
                        Surface(
                            color = if (hasFestive) Color.Red.copy(alpha = 0.1f) else Color.Transparent,
                            shape = RoundedCornerShape(8.dp),
                            modifier = Modifier.padding(bottom = 12.dp, start = 16.dp, end = 16.dp)
                        ) {
                            Row(modifier = Modifier.padding(8.dp), verticalAlignment = Alignment.CenterVertically) {
                                Box(modifier = Modifier.size(12.dp).background(Color.Red, RoundedCornerShape(2.dp)))
                                Spacer(Modifier.width(8.dp))
                                Text(
                                    text = if (hasFestive) "RANGE INCLUDE FESTIVI: Tariffa Festiva applicata" else "Domeniche e festivi inclusi saranno in Tariffa Festiva",
                                    style = MaterialTheme.typography.labelSmall,
                                    color = Color.Red,
                                    fontWeight = if (hasFestive) FontWeight.Bold else FontWeight.Normal
                                )
                            }
                        }
                    }
                }
            }

            Spacer(Modifier.height(16.dp))

            if (searchResults.isEmpty()) {
                Box(modifier = Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
                    Text(
                        if (searchQuery.length < 2) stringResource(Res.string.type_min_chars) else stringResource(Res.string.no_pharmacists_found),
                        color = Color.Gray
                    )
                }
            } else {
                LazyColumn(verticalArrangement = Arrangement.spacedBy(8.dp)) {
                    items(searchResults.take(if (viewModel.accessoCompleto) searchResults.size else 3)) { farmacista ->
                        FarmacistaResultItem(farmacista, isBlurred = false, viewModel = viewModel)
                    }
                    
                    if (!viewModel.accessoCompleto && searchResults.size > 3) {
                        items(searchResults.drop(3)) { farmacista ->
                            FarmacistaResultItem(farmacista, isBlurred = true, viewModel = viewModel)
                        }
                    }
                }
            }
        }
    }
}

@Composable
fun FarmacistaResultItem(farmacista: Professionista, isBlurred: Boolean, viewModel: AuthViewModel) {
    var showDetails by remember { mutableStateOf(false) }
    val uriHandler = LocalUriHandler.current

    Card(
        modifier = Modifier.fillMaxWidth().clickable { 
            if (!isBlurred) showDetails = true 
        },
        elevation = CardDefaults.cardElevation(defaultElevation = 2.dp),
        colors = CardDefaults.cardColors(containerColor = Color.White),
        shape = RoundedCornerShape(8.dp)
    ) {
        Box {
            Row(
                modifier = Modifier
                    .padding(16.dp)
                    .then(if (isBlurred) Modifier.blur(8.dp) else Modifier), 
                verticalAlignment = Alignment.CenterVertically
            ) {
                Icon(Icons.Default.Person, contentDescription = null, tint = Color(0xFF1976D2))
                Spacer(Modifier.width(12.dp))
                Column {
                    Text("${farmacista.nome} ${farmacista.cognome}", fontWeight = FontWeight.Bold)
                    Text("${stringResource(Res.string.vat_number)}: ${farmacista.partitaIva}", style = MaterialTheme.typography.bodySmall, color = Color.Gray)
                    
                    if (farmacista.competenze.isNotEmpty()) {
                        Spacer(Modifier.height(4.dp))
                        Row(horizontalArrangement = Arrangement.spacedBy(4.dp)) {
                            farmacista.competenze.take(3).forEach { skill ->
                                Surface(
                                    color = MaterialTheme.colorScheme.primaryContainer.copy(alpha = 0.3f),
                                    shape = RoundedCornerShape(4.dp)
                                ) {
                                    Text(
                                        text = skill,
                                        modifier = Modifier.padding(horizontal = 4.dp, vertical = 2.dp),
                                        fontSize = 9.sp,
                                        color = MaterialTheme.colorScheme.primary
                                    )
                                }
                            }
                            if (farmacista.competenze.size > 3) {
                                Text("+${farmacista.competenze.size - 3}", fontSize = 9.sp, color = Color.Gray)
                            }
                        }
                    }
                }
            }
            
            if (isBlurred) {
                Box(
                    modifier = Modifier
                        .matchParentSize()
                        .background(Color.Black.copy(alpha = 0.3f)),
                    contentAlignment = Alignment.Center
                ) {
                    PremiumGuard(viewModel) {}
                }
            }
        }
    }

    if (showDetails && !isBlurred) {
        val contactSubject = stringResource(Res.string.contact_subject)
        val contactBody = stringResource(Res.string.contact_body, farmacista.nome)
        
        AlertDialog(
            onDismissRequest = { showDetails = false },
            confirmButton = {
                Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.End) {
                    // Tasto Email (Sempre presente)
                    Button(
                        onClick = { 
                            uriHandler.openUri("mailto:${farmacista.email}?subject=${contactSubject.replace(" ", "%20")}&body=${contactBody.replace(" ", "%20")}")
                        },
                        colors = ButtonDefaults.buttonColors(containerColor = Color(0xFF1976D2)),
                        modifier = Modifier.padding(end = 8.dp)
                    ) {
                        Icon(Icons.Default.Email, null, modifier = Modifier.size(18.dp))
                        Spacer(Modifier.width(8.dp))
                        Text(stringResource(Res.string.email_button))
                    }

                    // Tasto Chiama (Solo se c'è il numero)
                    if (!farmacista.telefono.isNullOrBlank()) {
                        Button(
                            onClick = { 
                                try {
                                    uriHandler.openUri("tel:${farmacista.telefono}")
                                } catch (e: Exception) {
                                    println("Errore dialer: ${e.message}")
                                }
                            },
                            colors = ButtonDefaults.buttonColors(containerColor = Color(0xFF4CAF50)),
                            modifier = Modifier.padding(end = 8.dp)
                        ) {
                            Icon(Icons.Default.Phone, null, modifier = Modifier.size(18.dp))
                            Spacer(Modifier.width(8.dp))
                            Text(stringResource(Res.string.call_button))
                        }
                    }
                    
                    TextButton(onClick = { showDetails = false }) {
                        Text(stringResource(Res.string.close_button))
                    }
                }
            },
            title = { Text(if (farmacista is Infermiere) "Dettagli infermiere" else stringResource(Res.string.pharmacist_details_title), fontWeight = FontWeight.Bold) },
            text = {
                Column(verticalArrangement = Arrangement.spacedBy(12.dp)) {
                    DetailRow(Icons.Default.Person, stringResource(Res.string.nominative_label), "${farmacista.nome} ${farmacista.cognome}")
                    DetailRow(Icons.Default.Email, stringResource(Res.string.email_row), farmacista.email)
                    DetailRow(Icons.Default.Info, stringResource(Res.string.vat_number), farmacista.partitaIva)
                    DetailRow(Icons.Default.AccountBox, stringResource(Res.string.tax_code_row), farmacista.codiceFiscale ?: stringResource(Res.string.not_specified))
                    if (farmacista is Infermiere) {
                        DetailRow(Icons.Default.Info, "N. iscrizione OPI", farmacista.numeroIscrizioneOpi ?: stringResource(Res.string.not_specified))
                        DetailRow(
                            Icons.Default.Info, "Polizza RC",
                            farmacista.polizzaRcNumero?.let { n -> n + (farmacista.polizzaRcScadenza?.let { " (scade il ${dataItaliana(it)})" } ?: "") }
                                ?: stringResource(Res.string.not_specified)
                        )
                    }
                    
                    if (!farmacista.telefono.isNullOrBlank()) {
                        DetailRow(Icons.Default.Phone, stringResource(Res.string.phone_row), farmacista.telefono!!)
                    }
                }
            }
        )
    }
}

@Composable
fun DetailRow(icon: ImageVector, label: String, value: String) {
    Row(verticalAlignment = Alignment.CenterVertically, modifier = Modifier.fillMaxWidth()) {
        Icon(icon, null, modifier = Modifier.size(20.dp), tint = Color(0xFF1976D2))
        Spacer(Modifier.width(12.dp))
        Column {
            Text(label, style = MaterialTheme.typography.labelSmall, color = Color.Gray)
            Text(value, style = MaterialTheme.typography.bodyMedium, fontWeight = FontWeight.Medium)
        }
    }
}
